import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Params } from '@angular/router';
import { PhoneFilterOptions } from '@app/akita/api/products/models/phone-filter-options.model';
import { ProductSearchFilterOptions } from '@app/akita/api/products/models/product-search.filter-options.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaProductsService } from '@app/akita/api/products/state/products.service';
import { AkitaStartingPricesQuery } from '@app/akita/api/starting-prices/state/starting-prices.query';
import { AkitaStartingPricesService } from '@app/akita/api/starting-prices/state/starting-prices.service';

import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { ApiError } from '@app/shared/models/api/api-error.model';
import { Observable, Subject, Subscription, combineLatest, map, of } from 'rxjs';
import { PopsySkeletonComponent } from '../../../components/skeleton/popsy-skeleton.component';
import { AsyncPipe } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';

@Component({
  selector: 'app-product-category-filter',
  templateUrl: './product-category-filter.template.html',
  styleUrls: ['./product-category-filter.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, PopsySkeletonComponent, AsyncPipe],
})
export class ProductCategoryFilterComponent implements OnDestroy, OnChanges {
  @Input() public phoneFilterOptions?: PhoneFilterOptions | null;
  @Input() public mobileMode?: boolean | null = false;
  @Input() public gridMode?: boolean | null = false;
  @Input() public pillMode?: boolean | null = false;

  @Input() public loading?: boolean | null;

  @Output() public readonly filterCategoryChanged: EventEmitter<Array<string>>;

  public readonly phoneSearchFragment$: Observable<Array<string>>;
  public country: string;

  public productOptions$: Observable<ProductSearchFilterOptions | null>;
  public readonly fetchingProductOptions$: Observable<boolean>;
  public readonly productOptionsError$: Observable<ApiError | null>;

  public readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  // public phoneProductFragment: Array<string>;
  public readonly queryParamsToKeep$: Observable<Params>;

  constructor(
    private readonly zone: NgZone,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaProductsService: AkitaProductsService,
    private readonly akitaStartingPricesQuery: AkitaStartingPricesQuery,
    private readonly akitaStartingPricesService: AkitaStartingPricesService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();

    this.filterCategoryChanged = new EventEmitter();

    this.country = this.validateCountryCode(this.akitaRouterQuery.country);

    this.phoneSearchFragment$ = this.akitaRouterQuery.selectPhoneSearchFragment();

    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();

    this.productOptions$ = of(null);
    this.fetchingProductOptions$ =
      this.akitaProductsQuery.selectIsFetchingProductFilters();
    this.productOptionsError$ =
      this.akitaProductsQuery.selectErrorFetchingProductFilters();

    if (this.akitaRouterQuery.isBrowser) {
      this.zone.runOutsideAngular(this.fetchProductOptionsInBG.bind(this));
      this.zone.runOutsideAngular(this.fetchProductOptionsInBG.bind(this));
    } else {
      this.fetchProductOptionsInBG();
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      this.productOptions$ = combineLatest([
        this.akitaProductsQuery.selectProductFilters(),
        this.akitaStartingPricesQuery.selectStartingPrices(),
      ]).pipe(
        map(([options, startingPrices]) => {
          const ops = JSON.parse(JSON.stringify(options));

          if (ops.category.includes('WATCH')) {
            ops.category.splice(ops.category.indexOf('WATCH'), 1);
            if (startingPrices.find((x) => x.category === 'WATCH')) {
              ops.category.push('WATCH');
            }
          }
          if (ops.category.includes('HEADPHONES')) {
            ops.category.splice(ops.category.indexOf('HEADPHONES'), 1);
            ops.category.push('HEADPHONES');
          }
          if (ops.category.includes('GAMING DESKTOP')) {
            ops.category.splice(ops.category.indexOf('GAMING DESKTOP'), 1);
            if (startingPrices.find((x) => x.category === 'GAMING DESKTOP')) {
              ops.category.push('GAMING DESKTOP');
            }
          }
          if (ops.category.includes('ACCESSORIES')) {
            ops.category.splice(ops.category.indexOf('ACCESSORIES'), 1);
            if (startingPrices.findIndex((x) => x.category === 'ACCESSORIES') !== -1) {
              ops.category.push('ACCESSORIES');
            }
          }
          return ops;
        })
      );
    }
  }

  private fetchProductOptionsInBG(): void {
    this.subscriptions.add(this.akitaProductsService.getAvailableSearchOptionsAsync());
    this.subscriptions.add(
      this.akitaStartingPricesService.findStartingPricesAsync(this.country)
    );
  }

  public validateCountryCode(country: string): string {
    if (country) {
      country = country.toUpperCase() || '';
      if (
        country !== 'SA' &&
        country !== 'AE' &&
        country !== 'OM' &&
        country !== 'KW' &&
        country !== 'US' &&
        country !== 'MX' &&
        country !== 'CA' &&
        country !== 'BH'
      ) {
        country = 'US'; // fallback
      }
    }
    return country;
  }

  public switchCategoryFilter(category?: string | null): void {
    // MULTIPLE SELECTION NOT AVAILABLE
    if (category === 'label_all') {
      // this.selectedModelsSubject.next([]);
      this.filterCategoryChanged.emit([]);
    } else if (category) {
      const selectedCategories = new Array(0);
      const position = this.phoneFilterOptions?.category?.indexOf(category) || -1;
      if (position === -1) {
        selectedCategories.push(category);
      } else if (position !== -1) {
        selectedCategories.splice(position, 1);
      }
      // this.selectedModelsSubject.next(selectedModels);
      this.filterCategoryChanged.emit(selectedCategories);
    }

    this.changeDetector.markForCheck();
  }

  public productFragment(id: number | null): Array<string> {
    return this.akitaRouterQuery.getProductUrlFragment(`${id}`);
  }

  public trackByModel(index: number, category?: any | null): string {
    return (category as any)?.id || category || `${index}`;
  }

  public capitalizeCategory(str: string): string | null {
    return str
      .charAt(0)
      .toUpperCase()
      .concat(str.slice(1).replace(/ /g, '_').toLowerCase());
  }

  public getSearchUrlFragment(): Array<string> | string {
    return this.akitaRouterQuery.getPhoneSearchFragment();
  }

  public getCategoryParams(category: string): Params | null {
    return {
      ...this.akitaRouterQuery.queryParamsToKeep,
      category: category,
    };
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
